import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session, UserRole } from '../lib/provider/types';
import { useProvider } from '../lib/provider';

interface AuthContextType {
    session: Session;
    isLoading: boolean;
    signInMock: (role: UserRole, householdId: string, displayName: string) => Promise<void>;
    signInReal: (email: string, pass: string) => Promise<void>;
    signUpReal: (email: string, pass: string) => Promise<void>;
    createHousehold: (name: string, founderName: string) => Promise<void>;
    joinHousehold: (householdId: string, displayName: string, role: UserRole) => Promise<void>;
    signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const provider = useProvider();
    const [session, setSession] = useState<Session>({ user: null, isAuthenticated: false });
    const [isLoading, setIsLoading] = useState(true);

    const refreshSession = async () => {
        try {
            const s = await provider.getSession();
            setSession(s);
        } catch (e) {
            console.error('Auth check failed', e);
        }
    };

    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            await refreshSession();
            setIsLoading(false);
        };
        init();
    }, [provider]);

    const signInMock = async (role: UserRole, householdId: string, displayName: string) => {
        const s = await provider.signInMock(role, householdId, displayName);
        setSession(s);
    };

    const signInReal = async (email: string, pass: string) => {
        if (!provider.signIn) throw new Error('Sign in not supported by provider');
        const s = await provider.signIn(email, pass);
        setSession(s);
    };

    const signUpReal = async (email: string, pass: string) => {
        if (!provider.signUp) throw new Error('Sign up not supported by provider');
        await provider.signUp(email, pass);
        // After signup with Supabase, the user might need to confirm email or just sign in.
        // For simplicity, we assume they can sign in now or it's auto-signin.
    };

    const createHousehold = async (name: string, founderName: string) => {
        if (!provider.createHousehold) throw new Error('Household creation not supported by provider');
        const s = await provider.createHousehold(name, founderName);
        setSession(s);
    };

    const joinHousehold = async (householdId: string, displayName: string, role: UserRole) => {
        if (!provider.joinHousehold) throw new Error('Join household not supported by provider');
        const s = await provider.joinHousehold(householdId, displayName, role);
        setSession(s);
    };

    const signOut = async () => {
        await provider.signOut();
        setSession({ user: null, isAuthenticated: false });
    };

    return (
        <AuthContext.Provider value={{ session, isLoading, signInMock, signInReal, signUpReal, createHousehold, joinHousehold, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};
